import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  VERTEX360_COPILOT_CONVERSATIONS_PAGE_SIZE,
  VERTEX360_COPILOT_MESSAGES_PAGE_SIZE,
  VERTEX360_COPILOT_SIDEBAR_COLLAPSED_KEY,
  VERTEX360_COPILOT_WORKSPACE_VIEWS,
  createConversationId,
  deriveConversationTitle,
  displayConversationTitle,
  conversationTitleHasIdentityLeak,
  sortConversationsByActivity,
} from "../constants/vertex360CopilotWorkspaceConstants";
import {
  createMessage,
  normalizeClarifyStreamRender,
} from "../utils/vertex360CopilotChatUtils";
import { normalizeCopilotDegradedPayload } from "../utils/copilotDegradedMode";
import { createCopilotStreamSession } from "../utils/copilotStreamSession";
import { resolveVertex360CopilotErrorMessage } from "../utils/vertex360CopilotErrorUtils";
import {
  showCopilotDeleteSuccessToast,
  showCopilotRenameErrorToast,
  showCopilotRenameSuccessToast,
  showCopilotStarErrorToast,
  showCopilotStarredToast,
  showCopilotUnstarredToast,
} from "../utils/vertex360CopilotToastUtils";
import { vertex360CopilotApi } from "store/services/vertex360Copilot.services";
import { vertex360CopilotConversationApi } from "store/services/vertex360CopilotConversation.services";

const WORKSPACE_ACTIONS = {
  SET_SIDEBAR_COLLAPSED: "SET_SIDEBAR_COLLAPSED",
  SET_VIEW: "SET_VIEW",
  SELECT_CONVERSATION: "SELECT_CONVERSATION",
  NEW_CHAT: "NEW_CHAT",
  SET_CONVERSATIONS: "SET_CONVERSATIONS",
  SET_CONVERSATION_SECTION_LOADING: "SET_CONVERSATION_SECTION_LOADING",
  MERGE_CONVERSATION_PAGE: "MERGE_CONVERSATION_PAGE",
  SET_CONVERSATION_MESSAGES: "SET_CONVERSATION_MESSAGES",
  REKEY_CONVERSATION: "REKEY_CONVERSATION",
  STAR_CONVERSATION: "STAR_CONVERSATION",
  UNSTAR_CONVERSATION: "UNSTAR_CONVERSATION",
  RENAME_CONVERSATION: "RENAME_CONVERSATION",
  DELETE_CONVERSATION: "DELETE_CONVERSATION",
  DELETE_CONVERSATIONS: "DELETE_CONVERSATIONS",
  SET_SEARCH_QUERY: "SET_SEARCH_QUERY",
  SET_SELECTED_IDS: "SET_SELECTED_IDS",
  TOGGLE_SELECT_ID: "TOGGLE_SELECT_ID",
  ADD_USER_MESSAGE: "ADD_USER_MESSAGE",
  ADD_ASSISTANT_MESSAGE: "ADD_ASSISTANT_MESSAGE",
  UPDATE_MESSAGE_CONTENT: "UPDATE_MESSAGE_CONTENT",
  SET_MESSAGE_RENDER: "SET_MESSAGE_RENDER",
  SET_MESSAGE_FEEDBACK: "SET_MESSAGE_FEEDBACK",
  SET_TYPING: "SET_TYPING",
  SET_STREAMING: "SET_STREAMING",
  PREPEND_OLDER_MESSAGES: "PREPEND_OLDER_MESSAGES",
  SET_LOADING_OLDER: "SET_LOADING_OLDER",
  SET_HAS_MORE_OLDER: "SET_HAS_MORE_OLDER",
  SET_MESSAGES_PAGE_SIZE: "SET_MESSAGES_PAGE_SIZE",
  REMOVE_MESSAGE: "REMOVE_MESSAGE",
  PRUNE_DRAFT_CONVERSATIONS: "PRUNE_DRAFT_CONVERSATIONS",
  SET_CONVERSATION_FILTERS: "SET_CONVERSATION_FILTERS",
};

/** Empty local thread — not listed in the sidebar until the first message is sent. */
const isDraftConversation = (conversation) =>
  Boolean(
    conversation &&
      !conversation.serverId &&
      (!conversation.messages || conversation.messages.length === 0)
  );

const withoutDraftConversations = (conversations) =>
  (conversations || []).filter((conversation) => !isDraftConversation(conversation));

/** Map a Laravel conversation resource to the local sidebar shape. */
const mapServerConversation = (c) => ({
  id: c.uuid || c.id,
  serverId: c.uuid || c.id,
  title: displayConversationTitle(c.title),
  starred: Boolean(c.starred),
  createdAt: c.created_at,
  updatedAt: c.last_message_at || c.updated_at || c.created_at,
  messages: [],
  messagesLoaded: false,
  activeFilters:
    c.active_filters && typeof c.active_filters === "object"
      ? c.active_filters
      : null,
  contextSummary:
    typeof c.context_summary === "string" && c.context_summary.trim()
      ? c.context_summary.trim()
      : null,
});

const emptySectionMeta = (overrides = {}) => ({
  page: 0,
  hasMore: true,
  isLoading: false,
  isLoadingMore: false,
  loaded: false,
  ...overrides,
});

/** Merge server page into local list; keep in-memory messages for known threads. */
const mergeConversationPage = (existing, incoming) => {
  const byId = new Map();
  (existing || []).forEach((conversation) => {
    byId.set(conversation.id, conversation);
  });
  (incoming || []).forEach((conversation) => {
    const prev = byId.get(conversation.id);
    if (prev) {
      byId.set(conversation.id, {
        ...prev,
        ...conversation,
        // Keep in-memory thread content across sidebar page fetches.
        messages: prev.messages,
        messagesLoaded: prev.messagesLoaded,
        // Keep optimistic star/title until a full reload.
        starred: prev.starred,
        title: prev.title || conversation.title,
        // Prefer freshest non-PII filter chips from the server page.
        activeFilters:
          conversation.activeFilters !== undefined
            ? conversation.activeFilters
            : prev.activeFilters,
        contextSummary:
          conversation.contextSummary !== undefined
            ? conversation.contextSummary
            : prev.contextSummary,
      });
    } else {
      byId.set(conversation.id, conversation);
    }
  });
  return Array.from(byId.values());
};

const normalizeFeedbackRating = (value) => {
  const cleaned = String(value || "").toLowerCase().trim();
  return cleaned === "up" || cleaned === "down" ? cleaned : undefined;
};

/** Map a Laravel message resource to the local message shape. */
const mapServerMessage = (m) => {
  const feedbackRating = normalizeFeedbackRating(m.feedback_rating);
  let render = m.render || undefined;
  if (render?.type === "clarify") {
    render = normalizeClarifyStreamRender({ render }) || render;
  }
  const degraded = normalizeCopilotDegradedPayload(m);
  return {
    id: m.id,
    role: m.role,
    content: String(m.content || ""),
    createdAt: m.created_at,
    render,
    sources: Array.isArray(m.sources) && m.sources.length ? m.sources : undefined,
    suggestions: Array.isArray(m.suggestions)
      ? m.suggestions
      : Array.isArray(m.render?.suggestions)
        ? m.render.suggestions
        : [],
    ...(feedbackRating ? { feedbackRating } : {}),
    ...(degraded.isReducedMode
      ? {
          isReducedMode: true,
          degradeReason: degraded.degradeReason,
          servicePath: degraded.servicePath,
        }
      : {}),
  };
};

/**
 * Apply shared stream-session effects to the workspace reducer.
 * Local message ids stay stable; server uuid is stored as ``serverId``.
 *
 * @param {Function} dispatch
 * @param {object[]} effects
 * @param {{ conversationId: string, localAssistantMessageId: string, rekeyTitle?: string }} ctx
 */
const applyWorkspaceStreamEffects = (dispatch, effects, ctx) => {
  for (const effect of effects || []) {
    switch (effect.op) {
      case "conversationId":
        if (
          effect.conversationId &&
          effect.conversationId !== ctx.conversationId
        ) {
          dispatch({
            type: WORKSPACE_ACTIONS.REKEY_CONVERSATION,
            payload: {
              fromId: ctx.conversationId,
              toId: effect.conversationId,
              title: ctx.rekeyTitle,
            },
          });
          ctx.conversationId = effect.conversationId;
        }
        break;
      case "userMessageId":
        // Drawer rekeys the optimistic user bubble; workspace keeps local ids.
        break;
      case "assistantMessageId":
        if (effect.messageId) {
          dispatch({
            type: WORKSPACE_ACTIONS.SET_MESSAGE_RENDER,
            payload: {
              conversationId: ctx.conversationId,
              messageId: ctx.localAssistantMessageId,
              serverId: effect.messageId,
            },
          });
        }
        break;
      case "content":
        if (effect.clearTyping) {
          dispatch({ type: WORKSPACE_ACTIONS.SET_TYPING, payload: false });
        }
        dispatch({
          type: WORKSPACE_ACTIONS.UPDATE_MESSAGE_CONTENT,
          payload: {
            conversationId: ctx.conversationId,
            messageId: ctx.localAssistantMessageId,
            content: effect.content,
            isError: false,
          },
        });
        break;
      case "render":
        dispatch({
          type: WORKSPACE_ACTIONS.SET_MESSAGE_RENDER,
          payload: {
            conversationId: ctx.conversationId,
            messageId: ctx.localAssistantMessageId,
            render: effect.render,
            sources: effect.sources,
            suggestions: effect.suggestions,
            ...(effect.serverId ? { serverId: effect.serverId } : {}),
          },
        });
        break;
      case "sources":
        dispatch({
          type: WORKSPACE_ACTIONS.SET_MESSAGE_RENDER,
          payload: {
            conversationId: ctx.conversationId,
            messageId: ctx.localAssistantMessageId,
            sources: effect.sources,
          },
        });
        break;
      case "filters":
        dispatch({
          type: WORKSPACE_ACTIONS.SET_CONVERSATION_FILTERS,
          payload: {
            conversationId: ctx.conversationId,
            activeFilters: effect.activeFilters,
            contextSummary: effect.contextSummary,
          },
        });
        break;
      case "degraded":
        dispatch({
          type: WORKSPACE_ACTIONS.SET_MESSAGE_RENDER,
          payload: {
            conversationId: ctx.conversationId,
            messageId: ctx.localAssistantMessageId,
            isReducedMode: true,
            degradeReason: effect.degradeReason || null,
            servicePath: effect.servicePath || null,
          },
        });
        break;
      case "unbusyClarify":
      case "unbusyComplete":
        dispatch({ type: WORKSPACE_ACTIONS.SET_TYPING, payload: false });
        dispatch({
          type: WORKSPACE_ACTIONS.SET_STREAMING,
          payload: { isStreaming: false, streamingMessageId: null },
        });
        break;
      default:
        break;
    }
  }
};

const readSidebarCollapsed = () => {
  try {
    return sessionStorage.getItem(VERTEX360_COPILOT_SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

const writeSidebarCollapsed = (collapsed) => {
  try {
    sessionStorage.setItem(VERTEX360_COPILOT_SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    /* ignore */
  }
};

const touchConversation = (conversation, patch = {}) => ({
  ...conversation,
  ...patch,
  updatedAt: patch.updatedAt || new Date().toISOString(),
});

const initialState = {
  conversations: [],
  activeConversationId: null,
  view: VERTEX360_COPILOT_WORKSPACE_VIEWS.CHAT,
  sidebarCollapsed: readSidebarCollapsed(),
  searchQuery: "",
  selectedIds: [],
  isAssistantTyping: false,
  isStreaming: false,
  streamingMessageId: null,
  isLoadingOlder: false,
  isLoadingConversations: true,
  conversationSections: {
    // First paint: show section skeletons until page-1 requests finish.
    starred: emptySectionMeta({ isLoading: true }),
    recent: emptySectionMeta({ isLoading: true }),
  },
  olderPagesLoaded: {},
  messagesPageSize: VERTEX360_COPILOT_MESSAGES_PAGE_SIZE,
};

const workspaceReducer = (state, action) => {
  switch (action.type) {
    case WORKSPACE_ACTIONS.SET_SIDEBAR_COLLAPSED:
      return { ...state, sidebarCollapsed: Boolean(action.payload) };

    case WORKSPACE_ACTIONS.SET_VIEW:
      return {
        ...state,
        view: action.payload,
        selectedIds:
          action.payload === VERTEX360_COPILOT_WORKSPACE_VIEWS.MANAGE ? state.selectedIds : [],
      };

    case WORKSPACE_ACTIONS.SELECT_CONVERSATION:
      return {
        ...state,
        activeConversationId: action.payload,
        view: VERTEX360_COPILOT_WORKSPACE_VIEWS.CHAT,
        selectedIds: [],
        conversations: withoutDraftConversations(state.conversations),
      };

    case WORKSPACE_ACTIONS.PRUNE_DRAFT_CONVERSATIONS: {
      const keepId = action.payload?.keepId;
      return {
        ...state,
        conversations: state.conversations.filter(
          (conversation) =>
            !isDraftConversation(conversation) || conversation.id === keepId
        ),
      };
    }

    case WORKSPACE_ACTIONS.NEW_CHAT: {
      const newConversation = {
        id: action.payload.id,
        serverId: null,
        title: "New chat",
        starred: false,
        createdAt: action.payload.createdAt,
        updatedAt: action.payload.createdAt,
        messages: [],
        messagesLoaded: true,
      };
      return {
        ...state,
        conversations: [
          newConversation,
          ...withoutDraftConversations(state.conversations),
        ],
        activeConversationId: newConversation.id,
        view: VERTEX360_COPILOT_WORKSPACE_VIEWS.CHAT,
        selectedIds: [],
        isAssistantTyping: false,
        isStreaming: false,
        streamingMessageId: null,
        olderPagesLoaded: {
          ...state.olderPagesLoaded,
          [newConversation.id]: { hasMore: false, page: 0 },
        },
      };
    }

    case WORKSPACE_ACTIONS.SET_CONVERSATIONS:
      return {
        ...state,
        conversations: Array.isArray(action.payload) ? action.payload : [],
        isLoadingConversations: false,
      };

    case WORKSPACE_ACTIONS.SET_CONVERSATION_SECTION_LOADING: {
      const { section, phase, loading } = action.payload || {};
      if (section !== "starred" && section !== "recent") return state;
      const current = state.conversationSections[section] || emptySectionMeta();
      return {
        ...state,
        conversationSections: {
          ...state.conversationSections,
          [section]: {
            ...current,
            ...(phase === "more"
              ? { isLoadingMore: Boolean(loading) }
              : { isLoading: Boolean(loading) }),
          },
        },
      };
    }

    case WORKSPACE_ACTIONS.MERGE_CONVERSATION_PAGE: {
      const { section, conversations, page, hasMore } = action.payload || {};
      if (section !== "starred" && section !== "recent") return state;
      const nextSections = {
        ...state.conversationSections,
        [section]: {
          ...(state.conversationSections[section] || emptySectionMeta()),
          page: Number(page) || 1,
          hasMore: Boolean(hasMore),
          isLoading: false,
          isLoadingMore: false,
          loaded: true,
        },
      };
      const bothLoaded =
        nextSections.starred.loaded && nextSections.recent.loaded;
      return {
        ...state,
        conversations: mergeConversationPage(
          state.conversations,
          Array.isArray(conversations) ? conversations : []
        ),
        conversationSections: nextSections,
        isLoadingConversations: !bothLoaded,
      };
    }

    case WORKSPACE_ACTIONS.SET_CONVERSATION_MESSAGES: {
      const { conversationId, messages } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          const firstUser = (messages || []).find(
            (item) => item?.role === "user" && String(item.content || "").trim()
          );
          const nextTitle = conversationTitleHasIdentityLeak(conversation.title)
            ? displayConversationTitle(conversation.title, firstUser?.content)
            : conversation.title;
          return {
            ...conversation,
            messages,
            messagesLoaded: true,
            title: nextTitle,
          };
        }),
      };
    }

    case WORKSPACE_ACTIONS.REKEY_CONVERSATION: {
      const { fromId, toId, title } = action.payload;
      return {
        ...state,
        activeConversationId:
          state.activeConversationId === fromId ? toId : state.activeConversationId,
        streamingMessageId: state.streamingMessageId,
        conversations: state.conversations.map((conversation) =>
          conversation.id === fromId
            ? {
                ...conversation,
                id: toId,
                serverId: toId,
                messagesLoaded: true,
                title: title || conversation.title,
              }
            : conversation
        ),
        olderPagesLoaded: {
          ...state.olderPagesLoaded,
          [toId]: state.olderPagesLoaded[fromId] || { hasMore: false, page: 0 },
        },
      };
    }

    case WORKSPACE_ACTIONS.SET_MESSAGE_RENDER: {
      const {
        conversationId,
        messageId,
        render,
        sources,
        suggestions,
        serverId,
        isReducedMode,
        degradeReason,
        servicePath,
      } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          return {
            ...conversation,
            messages: conversation.messages.map((message) =>
              message.id === messageId
                ? {
                    ...message,
                    ...(render !== undefined ? { render } : {}),
                    ...(sources !== undefined ? { sources } : {}),
                    ...(suggestions !== undefined ? { suggestions } : {}),
                    ...(serverId !== undefined && serverId !== null
                      ? { serverId: String(serverId) }
                      : {}),
                    ...(isReducedMode !== undefined ? { isReducedMode } : {}),
                    ...(degradeReason !== undefined ? { degradeReason } : {}),
                    ...(servicePath !== undefined ? { servicePath } : {}),
                  }
                : message
            ),
          };
        }),
      };
    }

    case WORKSPACE_ACTIONS.SET_MESSAGE_FEEDBACK: {
      const { conversationId, messageId, feedbackRating } = action.payload;
      const rating =
        feedbackRating === "up" || feedbackRating === "down"
          ? feedbackRating
          : null;
      if (!rating) return state;
      return {
        ...state,
        conversations: state.conversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          return {
            ...conversation,
            messages: conversation.messages.map((message) =>
              String(message.id) === String(messageId)
                ? { ...message, feedbackRating: rating }
                : message
            ),
          };
        }),
      };
    }

    case WORKSPACE_ACTIONS.STAR_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.map((conversation) =>
          conversation.id === action.payload
            ? touchConversation(conversation, {
                starred: true,
                updatedAt: conversation.updatedAt,
              })
            : conversation
        ),
      };

    case WORKSPACE_ACTIONS.UNSTAR_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.map((conversation) =>
          conversation.id === action.payload
            ? touchConversation(conversation, { starred: false })
            : conversation
        ),
      };

    case WORKSPACE_ACTIONS.RENAME_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.map((conversation) =>
          conversation.id === action.payload.id
            ? touchConversation(conversation, {
                title: action.payload.title,
                updatedAt: conversation.updatedAt,
              })
            : conversation
        ),
      };

    case WORKSPACE_ACTIONS.DELETE_CONVERSATION: {
      const nextConversations = state.conversations.filter(
        (conversation) => conversation.id !== action.payload
      );
      const wasActive = state.activeConversationId === action.payload;
      return {
        ...state,
        conversations: nextConversations,
        activeConversationId: wasActive ? null : state.activeConversationId,
        selectedIds: state.selectedIds.filter((id) => id !== action.payload),
      };
    }

    case WORKSPACE_ACTIONS.DELETE_CONVERSATIONS: {
      const ids = new Set(action.payload || []);
      const nextConversations = state.conversations.filter(
        (conversation) => !ids.has(conversation.id)
      );
      const activeRemoved = ids.has(state.activeConversationId);
      return {
        ...state,
        conversations: nextConversations,
        activeConversationId: activeRemoved ? null : state.activeConversationId,
        selectedIds: [],
      };
    }

    case WORKSPACE_ACTIONS.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };

    case WORKSPACE_ACTIONS.SET_SELECTED_IDS:
      return { ...state, selectedIds: action.payload };

    case WORKSPACE_ACTIONS.TOGGLE_SELECT_ID: {
      const id = action.payload;
      const exists = state.selectedIds.includes(id);
      return {
        ...state,
        selectedIds: exists
          ? state.selectedIds.filter((item) => item !== id)
          : [...state.selectedIds, id],
      };
    }

    case WORKSPACE_ACTIONS.ADD_USER_MESSAGE: {
      const { conversationId, message, title } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          const isFirstMessage = conversation.messages.length === 0;
          return touchConversation(conversation, {
            title: isFirstMessage ? title : conversation.title,
            messages: [...conversation.messages, message],
          });
        }),
      };
    }

    case WORKSPACE_ACTIONS.ADD_ASSISTANT_MESSAGE: {
      const { conversationId, message } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conversation) =>
          conversation.id === conversationId
            ? touchConversation(conversation, {
                messages: [...conversation.messages, message],
              })
            : conversation
        ),
      };
    }

    case WORKSPACE_ACTIONS.UPDATE_MESSAGE_CONTENT: {
      const { conversationId, messageId, content, isError } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          return {
            ...conversation,
            messages: conversation.messages.map((message) => {
              if (String(message.id) !== String(messageId)) return message;
              const next = { ...message, content };
              if (isError !== undefined) {
                next.isError = isError;
              }
              return next;
            }),
          };
        }),
      };
    }

    case WORKSPACE_ACTIONS.SET_CONVERSATION_FILTERS: {
      const { conversationId, activeFilters, contextSummary } = action.payload || {};
      return {
        ...state,
        conversations: state.conversations.map((conversation) =>
          conversation.id === conversationId
            ? {
                ...conversation,
                activeFilters: activeFilters || null,
                contextSummary: contextSummary || null,
              }
            : conversation
        ),
      };
    }

    case WORKSPACE_ACTIONS.REMOVE_MESSAGE: {
      const { conversationId, messageId } = action.payload;
      const nextStreamingId =
        state.streamingMessageId != null &&
        String(state.streamingMessageId) === String(messageId)
          ? null
          : state.streamingMessageId;
      return {
        ...state,
        streamingMessageId: nextStreamingId,
        conversations: state.conversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          return {
            ...conversation,
            messages: conversation.messages.filter(
              (message) => String(message.id) !== String(messageId)
            ),
          };
        }),
      };
    }

    case WORKSPACE_ACTIONS.SET_TYPING:
      return { ...state, isAssistantTyping: Boolean(action.payload) };

    case WORKSPACE_ACTIONS.SET_STREAMING:
      return {
        ...state,
        isStreaming: Boolean(action.payload.isStreaming),
        streamingMessageId: action.payload.streamingMessageId ?? null,
      };

    case WORKSPACE_ACTIONS.PREPEND_OLDER_MESSAGES: {
      const { conversationId, messages } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conversation) =>
          conversation.id === conversationId
            ? { ...conversation, messages: [...messages, ...conversation.messages] }
            : conversation
        ),
      };
    }

    case WORKSPACE_ACTIONS.SET_LOADING_OLDER:
      return { ...state, isLoadingOlder: Boolean(action.payload) };

    case WORKSPACE_ACTIONS.SET_HAS_MORE_OLDER: {
      const { conversationId, hasMore, page, nextBeforeId } = action.payload;
      return {
        ...state,
        olderPagesLoaded: {
          ...state.olderPagesLoaded,
          [conversationId]: {
            ...(state.olderPagesLoaded[conversationId] || {}),
            hasMore,
            ...(page !== undefined ? { page } : {}),
            ...(nextBeforeId !== undefined ? { nextBeforeId } : {}),
          },
        },
      };
    }

    case WORKSPACE_ACTIONS.SET_MESSAGES_PAGE_SIZE:
      return {
        ...state,
        messagesPageSize:
          Number(action.payload) > 0 ? Number(action.payload) : state.messagesPageSize,
      };

    default:
      return state;
  }
};

const Vertex360CopilotWorkspaceContext = createContext(null);

const CLOSED_DELETE_CONFIRM = {
  isOpen: false,
  variant: "single",
  conversations: [],
  isConfirming: false,
  error: null,
};

export const Vertex360CopilotWorkspaceProvider = ({ children }) => {
  const [state, dispatch] = useReducer(workspaceReducer, initialState);
  const [deleteConfirm, setDeleteConfirm] = useState(CLOSED_DELETE_CONFIRM);
  const [composerFocusKey, setComposerFocusKey] = useState(0);
  const scrollAnchorRef = useRef(null);
  const scrollRestoreRef = useRef(null);
  const generationAbortRef = useRef(false);
  const streamTimerRef = useRef(null);
  const streamAbortRef = useRef(null);
  const stateRef = useRef(state);
  const pendingNewConversationIdRef = useRef(null);
  const loadedConversationsRef = useRef(false);
  const sectionFetchLockRef = useRef({ starred: false, recent: false });

  stateRef.current = state;

  const activeConversation = useMemo(
    () =>
      state.conversations.find(
        (conversation) => conversation.id === state.activeConversationId
      ) || null,
    [state.conversations, state.activeConversationId]
  );

  const sidebarConversations = useMemo(
    () => state.conversations.filter((conversation) => !isDraftConversation(conversation)),
    [state.conversations]
  );

  const starredConversations = useMemo(
    () =>
      sortConversationsByActivity(
        sidebarConversations.filter((conversation) => conversation.starred)
      ),
    [sidebarConversations]
  );

  const recentConversations = useMemo(
    () =>
      sortConversationsByActivity(
        sidebarConversations.filter((conversation) => !conversation.starred)
      ),
    [sidebarConversations]
  );

  const allConversationsSorted = useMemo(
    () => sortConversationsByActivity(sidebarConversations),
    [sidebarConversations]
  );

  const filteredConversations = useMemo(() => {
    const query = state.searchQuery.trim().toLowerCase();
    if (!query) return allConversationsSorted;
    return allConversationsSorted.filter((conversation) =>
      conversation.title.toLowerCase().includes(query)
    );
  }, [allConversationsSorted, state.searchQuery]);

  const hasConversation = Boolean(activeConversation?.messages?.length);
  const isLoadingMessages = Boolean(
    activeConversation?.serverId && !activeConversation?.messagesLoaded
  );
  const hasMoreOlder = useMemo(() => {
    const conversationId = state.activeConversationId;
    if (!conversationId) return false;
    return Boolean(state.olderPagesLoaded[conversationId]?.hasMore);
  }, [state.activeConversationId, state.olderPagesLoaded]);

  const clearStreamTimer = useCallback(() => {
    if (streamTimerRef.current) {
      window.clearTimeout(streamTimerRef.current);
      streamTimerRef.current = null;
    }
  }, []);

  const stopGeneration = useCallback(() => {
    generationAbortRef.current = true;
    clearStreamTimer();
    if (streamAbortRef.current) {
      try {
        streamAbortRef.current.abort();
      } catch {
        /* ignore */
      }
      streamAbortRef.current = null;
    }
    dispatch({ type: WORKSPACE_ACTIONS.SET_TYPING, payload: false });
    dispatch({
      type: WORKSPACE_ACTIONS.SET_STREAMING,
      payload: { isStreaming: false, streamingMessageId: null },
    });
  }, [clearStreamTimer]);

  const sendMessage = useCallback(async (rawText, sendOptions = {}) => {
    const text = String(rawText || "").trim();
    const latestState = stateRef.current;
    if (!text || latestState.isAssistantTyping || latestState.isStreaming) {
      return false;
    }

    const {
      clarifyMessageId,
      clarifySelectedOption,
      clarifySelectedLabel,
      clarifyAnswers,
      clearActiveFilters,
    } = sendOptions || {};

    // Free-typed topic change dismisses unanswered clarify chips in the UI.
    const isClarifyAnswer =
      clarifyMessageId ||
      clarifyAnswers ||
      clarifySelectedOption ||
      /^\s*clarification\s*:/i.test(text);
    if (!isClarifyAnswer && latestState.activeConversationId) {
      const conv = latestState.conversations.find(
        (c) => c.id === latestState.activeConversationId
      );
      const openClarify = [...(conv?.messages || [])]
        .reverse()
        .find(
          (m) =>
            m?.role === "assistant" &&
            m?.render?.type === "clarify" &&
            !m?.render?.selected &&
            !m?.render?.answers &&
            !m?.render?.dismissed
        );
      if (openClarify?.id) {
        dispatch({
          type: WORKSPACE_ACTIONS.SET_MESSAGE_RENDER,
          payload: {
            conversationId: latestState.activeConversationId,
            messageId: openClarify.id,
            render: {
              ...(openClarify.render || {}),
              dismissed: true,
              selected: "__abandoned__",
            },
          },
        });
      }
    }

    // Resolve the target conversation; create a local one if starting fresh.
    let localId =
      pendingNewConversationIdRef.current || latestState.activeConversationId;
    let conversation = latestState.conversations.find((c) => c.id === localId);

    if (!localId || !conversation) {
      const createdAt = new Date().toISOString();
      localId = createConversationId();
      pendingNewConversationIdRef.current = localId;
      dispatch({
        type: WORKSPACE_ACTIONS.NEW_CHAT,
        payload: { id: localId, createdAt },
      });
      conversation = null;
    }
    pendingNewConversationIdRef.current = null;

    const serverId = conversation?.serverId || null;

    const userMessage = createMessage("user", text);
    dispatch({
      type: WORKSPACE_ACTIONS.ADD_USER_MESSAGE,
      payload: {
        conversationId: localId,
        message: userMessage,
        title: deriveConversationTitle(text),
      },
    });

    const assistantMessage = createMessage("assistant", "");
    dispatch({
      type: WORKSPACE_ACTIONS.ADD_ASSISTANT_MESSAGE,
      payload: { conversationId: localId, message: assistantMessage },
    });
    dispatch({ type: WORKSPACE_ACTIONS.SET_TYPING, payload: true });
    dispatch({
      type: WORKSPACE_ACTIONS.SET_STREAMING,
      payload: { isStreaming: true, streamingMessageId: assistantMessage.id },
    });

    generationAbortRef.current = false;
    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    const streamSession = createCopilotStreamSession({
      assistantMessageId: assistantMessage.id,
      conversationId: localId,
      userMessageId: userMessage.id,
    });
    const streamCtx = {
      conversationId: localId,
      localAssistantMessageId: assistantMessage.id,
      rekeyTitle: deriveConversationTitle(text),
    };

    try {
      await vertex360CopilotApi.sendMessageStream(text, {
        conversationId: serverId || undefined,
        clarifyMessageId,
        clarifySelectedOption,
        clarifySelectedLabel,
        clarifyAnswers,
        clearActiveFilters: Boolean(clearActiveFilters),
        signal: abortController.signal,
        onEvent: (payload) => {
          const { effects, error } = streamSession.handleEvent(payload);
          if (error) throw error;
          applyWorkspaceStreamEffects(dispatch, effects, streamCtx);
        },
      });

      if (!String(streamSession.content || "").trim()) {
        const emptyError = new Error("Vertex360 Copilot returned an empty response.");
        emptyError.code = "AI_UNAVAILABLE";
        throw emptyError;
      }

      return true;
    } catch (error) {
      if (error?.name === "AbortError" || error?.code === "CANCELLED") {
        return false;
      }
      if (!streamSession.content) {
        dispatch({
          type: WORKSPACE_ACTIONS.UPDATE_MESSAGE_CONTENT,
          payload: {
            conversationId: streamCtx.conversationId,
            messageId: assistantMessage.id,
            content: resolveVertex360CopilotErrorMessage(error),
            isError: true,
          },
        });
      }
      return false;
    } finally {
      streamAbortRef.current = null;
      dispatch({ type: WORKSPACE_ACTIONS.SET_TYPING, payload: false });
      dispatch({
        type: WORKSPACE_ACTIONS.SET_STREAMING,
        payload: { isStreaming: false, streamingMessageId: null },
      });
    }
  }, []);

  const retryMessage = useCallback(async (rawText) => {
    const text = String(rawText || "").trim();
    const latestState = stateRef.current;
    if (!text || latestState.isAssistantTyping || latestState.isStreaming) {
      return false;
    }

    const localId = latestState.activeConversationId;
    const conversation = latestState.conversations.find((item) => item.id === localId);
    if (!localId || !conversation) return false;

    const lastMessage = conversation.messages[conversation.messages.length - 1];
    if (lastMessage?.isError) {
      dispatch({
        type: WORKSPACE_ACTIONS.REMOVE_MESSAGE,
        payload: { conversationId: localId, messageId: lastMessage.id },
      });
    }

    const assistantMessage = createMessage("assistant", "");
    dispatch({
      type: WORKSPACE_ACTIONS.ADD_ASSISTANT_MESSAGE,
      payload: { conversationId: localId, message: assistantMessage },
    });
    dispatch({ type: WORKSPACE_ACTIONS.SET_TYPING, payload: true });
    dispatch({
      type: WORKSPACE_ACTIONS.SET_STREAMING,
      payload: { isStreaming: true, streamingMessageId: assistantMessage.id },
    });

    generationAbortRef.current = false;
    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    const streamSession = createCopilotStreamSession({
      assistantMessageId: assistantMessage.id,
      conversationId: localId,
    });
    const streamCtx = {
      conversationId: localId,
      localAssistantMessageId: assistantMessage.id,
      rekeyTitle: conversation.title,
    };

    try {
      await vertex360CopilotApi.sendMessageStream(text, {
        conversationId: conversation.serverId || undefined,
        signal: abortController.signal,
        onEvent: (payload) => {
          const { effects, error } = streamSession.handleEvent(payload);
          if (error) throw error;
          applyWorkspaceStreamEffects(dispatch, effects, streamCtx);
        },
      });

      if (!String(streamSession.content || "").trim()) {
        const emptyError = new Error("Vertex360 Copilot returned an empty response.");
        emptyError.code = "AI_UNAVAILABLE";
        throw emptyError;
      }

      return true;
    } catch (error) {
      if (error?.name === "AbortError" || error?.code === "CANCELLED") {
        return false;
      }
      if (!streamSession.content) {
        dispatch({
          type: WORKSPACE_ACTIONS.UPDATE_MESSAGE_CONTENT,
          payload: {
            conversationId: streamCtx.conversationId,
            messageId: assistantMessage.id,
            content: resolveVertex360CopilotErrorMessage(error),
            isError: true,
          },
        });
      }
      return false;
    } finally {
      streamAbortRef.current = null;
      dispatch({ type: WORKSPACE_ACTIONS.SET_TYPING, payload: false });
      dispatch({
        type: WORKSPACE_ACTIONS.SET_STREAMING,
        payload: { isStreaming: false, streamingMessageId: null },
      });
    }
  }, [dispatch]);

  const clearActiveFilters = useCallback(() => {
    const latestState = stateRef.current;
    if (latestState.isAssistantTyping || latestState.isStreaming) return false;
    const conversationId = latestState.activeConversationId;
    if (!conversationId) return false;
    dispatch({
      type: WORKSPACE_ACTIONS.SET_CONVERSATION_FILTERS,
      payload: {
        conversationId,
        activeFilters: null,
        contextSummary: null,
      },
    });
    return sendMessage("Clear the active filters for this conversation.", {
      clearActiveFilters: true,
    });
  }, [sendMessage]);

  /**
   * Optimistically render the user's clarify option choice. Persistence happens
   * server-side as part of the follow-up ``/chat/stream`` turn (see sendMessage),
   * so this only needs to update local state. Returns the message's server (DB)
   * id when known, so the caller can echo it back on the follow-up request.
   */
  const selectClarifyOption = useCallback(
    (message, optionId, selectedLabel, answers) => {
      if (!message || (!optionId && !answers)) return null;

      const state = stateRef.current;
      const conversation = state.conversations.find(
        (item) => item.id === state.activeConversationId
      );
      if (!conversation) return null;

      const target = conversation.messages.find((item) => item.id === message.id);
      const existingRender = target?.render || message.render || {};
      if (existingRender.selected || existingRender.answers) return null;

      const nextRender = { ...existingRender };
      if (optionId && optionId !== "__typed__") {
        nextRender.selected = optionId;
      }
      if (selectedLabel) {
        nextRender.selected_label = String(selectedLabel).trim();
      }
      if (answers && typeof answers === "object") {
        nextRender.answers = answers;
      }

      dispatch({
        type: WORKSPACE_ACTIONS.SET_MESSAGE_RENDER,
        payload: {
          conversationId: conversation.id,
          messageId: message.id,
          render: nextRender,
        },
      });

      return (
        target?.serverId ||
        message.serverId ||
        (/^\d+$/.test(String(message.id)) ? String(message.id) : null)
      );
    },
    []
  );

  const loadOlderMessages = useCallback(async () => {
    const latest = stateRef.current;
    const conversationId = latest.activeConversationId;
    const conversation = latest.conversations.find((item) => item.id === conversationId);
    const pageInfo = latest.olderPagesLoaded[conversationId];

    if (
      !conversationId ||
      !conversation ||
      !conversation.serverId ||
      latest.isLoadingOlder ||
      pageInfo?.hasMore === false ||
      !conversation.messages.length
    ) {
      return;
    }

    const beforeId = pageInfo?.nextBeforeId || conversation.messages[0]?.id;
    if (!beforeId) return;

    const listEl = scrollAnchorRef.current;
    scrollRestoreRef.current = {
      previousScrollHeight: listEl?.scrollHeight || 0,
      previousScrollTop: listEl?.scrollTop || 0,
    };

    dispatch({ type: WORKSPACE_ACTIONS.SET_LOADING_OLDER, payload: true });

    try {
      const response = await vertex360CopilotConversationApi.getMessages(
        conversation.serverId,
        {
          beforeId,
          limit: VERTEX360_COPILOT_MESSAGES_PAGE_SIZE,
        }
      );
      const older = (response?.messages || []).map(mapServerMessage);
      const page = (pageInfo?.page || 0) + 1;

      if (response?.page_size) {
        dispatch({
          type: WORKSPACE_ACTIONS.SET_MESSAGES_PAGE_SIZE,
          payload: response.page_size,
        });
      }

      dispatch({
        type: WORKSPACE_ACTIONS.PREPEND_OLDER_MESSAGES,
        payload: { conversationId, messages: older },
      });
      dispatch({
        type: WORKSPACE_ACTIONS.SET_HAS_MORE_OLDER,
        payload: {
          conversationId,
          hasMore: Boolean(response?.has_more_older),
          page,
          nextBeforeId: response?.next_before_id || null,
        },
      });
    } catch (error) {
      dispatch({
        type: WORKSPACE_ACTIONS.SET_HAS_MORE_OLDER,
        payload: { conversationId, hasMore: false },
      });
    } finally {
      dispatch({ type: WORKSPACE_ACTIONS.SET_LOADING_OLDER, payload: false });
    }
  }, []);

  useEffect(() => {
    if (state.isLoadingOlder || !scrollRestoreRef.current) return undefined;

    const { previousScrollHeight, previousScrollTop } = scrollRestoreRef.current;
    scrollRestoreRef.current = null;

    const frame = requestAnimationFrame(() => {
      const listEl = scrollAnchorRef.current;
      if (!listEl) return;
      const newScrollHeight = listEl.scrollHeight;
      listEl.scrollTop = newScrollHeight - previousScrollHeight + previousScrollTop;
    });

    return () => cancelAnimationFrame(frame);
  }, [state.isLoadingOlder, activeConversation?.messages]);

  useEffect(
    () => () => {
      generationAbortRef.current = true;
      clearStreamTimer();
      if (streamAbortRef.current) {
        try {
          streamAbortRef.current.abort();
        } catch {
          /* ignore */
        }
      }
    },
    [clearStreamTimer]
  );

  const fetchConversationSectionPage = useCallback(async (section, page) => {
    const api =
      section === "starred"
        ? vertex360CopilotConversationApi.listStarredConversations
        : vertex360CopilotConversationApi.listRecentConversations;
    const response = await api({
      page,
      limit: VERTEX360_COPILOT_CONVERSATIONS_PAGE_SIZE,
    });
    return {
      conversations: (response?.conversations || []).map(mapServerConversation),
      page: Number(response?.page) || page,
      hasMore: Boolean(response?.has_more),
    };
  }, []);

  const loadConversationSectionPage = useCallback(
    async (section, { reset = false } = {}) => {
      if (section !== "starred" && section !== "recent") return;
      if (sectionFetchLockRef.current[section]) return;

      const meta = stateRef.current.conversationSections[section] || emptySectionMeta();
      if (meta.isLoading || meta.isLoadingMore) return;
      if (!reset && (!meta.hasMore || !meta.loaded)) return;

      const nextPage = reset ? 1 : (meta.page || 0) + 1;
      const phase = reset || !meta.loaded ? "initial" : "more";
      sectionFetchLockRef.current[section] = true;

      dispatch({
        type: WORKSPACE_ACTIONS.SET_CONVERSATION_SECTION_LOADING,
        payload: { section, phase, loading: true },
      });

      try {
        const result = await fetchConversationSectionPage(section, nextPage);
        dispatch({
          type: WORKSPACE_ACTIONS.MERGE_CONVERSATION_PAGE,
          payload: {
            section,
            conversations: result.conversations,
            page: result.page,
            hasMore: result.hasMore,
          },
        });
      } catch {
        dispatch({
          type: WORKSPACE_ACTIONS.SET_CONVERSATION_SECTION_LOADING,
          payload: { section, phase, loading: false },
        });
        if (reset || !meta.loaded) {
          dispatch({
            type: WORKSPACE_ACTIONS.MERGE_CONVERSATION_PAGE,
            payload: {
              section,
              conversations: [],
              page: meta.page || 0,
              hasMore: false,
            },
          });
        }
      } finally {
        sectionFetchLockRef.current[section] = false;
      }
    },
    [fetchConversationSectionPage]
  );

  const loadMoreStarredConversations = useCallback(() => {
    loadConversationSectionPage("starred");
  }, [loadConversationSectionPage]);

  const loadMoreRecentConversations = useCallback(() => {
    loadConversationSectionPage("recent");
  }, [loadConversationSectionPage]);

  // Load first page of starred + recent on mount (separate APIs).
  useEffect(() => {
    if (loadedConversationsRef.current) return;
    loadedConversationsRef.current = true;

    let cancelledFetch = false;
    (async () => {
      dispatch({
        type: WORKSPACE_ACTIONS.SET_CONVERSATION_SECTION_LOADING,
        payload: { section: "starred", phase: "initial", loading: true },
      });
      dispatch({
        type: WORKSPACE_ACTIONS.SET_CONVERSATION_SECTION_LOADING,
        payload: { section: "recent", phase: "initial", loading: true },
      });

      const loadSection = async (section) => {
        try {
          const result = await fetchConversationSectionPage(section, 1);
          if (cancelledFetch) return;
          dispatch({
            type: WORKSPACE_ACTIONS.MERGE_CONVERSATION_PAGE,
            payload: {
              section,
              conversations: result.conversations,
              page: result.page,
              hasMore: result.hasMore,
            },
          });
        } catch {
          if (cancelledFetch) return;
          dispatch({
            type: WORKSPACE_ACTIONS.MERGE_CONVERSATION_PAGE,
            payload: {
              section,
              conversations: [],
              page: 0,
              hasMore: false,
            },
          });
        }
      };

      await Promise.all([loadSection("starred"), loadSection("recent")]);
    })();

    return () => {
      cancelledFetch = true;
    };
  }, [fetchConversationSectionPage]);

  // Lazy-load messages the first time a persisted conversation is opened.
  useEffect(() => {
    const conversationId = state.activeConversationId;
    if (!conversationId) return undefined;
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation || !conversation.serverId || conversation.messagesLoaded) {
      return undefined;
    }

    let cancelledFetch = false;
    (async () => {
      try {
        const response = await vertex360CopilotConversationApi.getMessages(
          conversation.serverId,
          {
            limit: VERTEX360_COPILOT_MESSAGES_PAGE_SIZE,
          }
        );
        if (cancelledFetch) return;
        const messages = (response?.messages || []).map(mapServerMessage);
        if (response?.page_size) {
          dispatch({
            type: WORKSPACE_ACTIONS.SET_MESSAGES_PAGE_SIZE,
            payload: response.page_size,
          });
        }
        dispatch({
          type: WORKSPACE_ACTIONS.SET_CONVERSATION_MESSAGES,
          payload: { conversationId, messages },
        });
        dispatch({
          type: WORKSPACE_ACTIONS.SET_HAS_MORE_OLDER,
          payload: {
            conversationId,
            hasMore: Boolean(response?.has_more_older),
            page: 1,
            nextBeforeId: response?.next_before_id || null,
          },
        });
      } catch {
        if (!cancelledFetch) {
          dispatch({
            type: WORKSPACE_ACTIONS.SET_CONVERSATION_MESSAGES,
            payload: { conversationId, messages: [] },
          });
        }
      }
    })();

    return () => {
      cancelledFetch = true;
    };
  }, [state.activeConversationId, state.conversations]);

  const setSidebarCollapsed = useCallback((collapsed) => {
    writeSidebarCollapsed(collapsed);
    dispatch({
      type: WORKSPACE_ACTIONS.SET_SIDEBAR_COLLAPSED,
      payload: collapsed,
    });
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(!state.sidebarCollapsed);
  }, [setSidebarCollapsed, state.sidebarCollapsed]);

  const setView = useCallback((view) => {
    dispatch({ type: WORKSPACE_ACTIONS.SET_VIEW, payload: view });
  }, []);

  const selectConversation = useCallback((conversationId) => {
    pendingNewConversationIdRef.current = null;
    dispatch({
      type: WORKSPACE_ACTIONS.SELECT_CONVERSATION,
      payload: conversationId,
    });
  }, []);

  const startNewChat = useCallback(() => {
    const latest = stateRef.current;
    const active = latest.conversations.find(
      (conversation) => conversation.id === latest.activeConversationId
    );

    if (isDraftConversation(active)) {
      pendingNewConversationIdRef.current = active.id;
      dispatch({
        type: WORKSPACE_ACTIONS.PRUNE_DRAFT_CONVERSATIONS,
        payload: { keepId: active.id },
      });
      dispatch({
        type: WORKSPACE_ACTIONS.SET_VIEW,
        payload: VERTEX360_COPILOT_WORKSPACE_VIEWS.CHAT,
      });
      setComposerFocusKey((key) => key + 1);
      return;
    }

    const id = createConversationId();
    pendingNewConversationIdRef.current = id;
    dispatch({
      type: WORKSPACE_ACTIONS.NEW_CHAT,
      payload: { id, createdAt: new Date().toISOString() },
    });
    setComposerFocusKey((key) => key + 1);
  }, []);

  const resolveServerId = useCallback((conversationId) => {
    const conversation = stateRef.current.conversations.find(
      (item) => item.id === conversationId
    );
    return conversation?.serverId || null;
  }, []);

  const rateMessageFeedback = useCallback((messageId, feedbackRating) => {
    const conversationId = stateRef.current.activeConversationId;
    if (!conversationId || !messageId) return;
    dispatch({
      type: WORKSPACE_ACTIONS.SET_MESSAGE_FEEDBACK,
      payload: { conversationId, messageId, feedbackRating },
    });
  }, []);

  const starConversation = useCallback(
    async (conversationId) => {
      dispatch({ type: WORKSPACE_ACTIONS.STAR_CONVERSATION, payload: conversationId });
      const serverId = resolveServerId(conversationId);
      if (!serverId) {
        showCopilotStarredToast();
        return;
      }

      try {
        await vertex360CopilotConversationApi.setStarred(serverId, true);
        showCopilotStarredToast();
      } catch {
        dispatch({
          type: WORKSPACE_ACTIONS.UNSTAR_CONVERSATION,
          payload: conversationId,
        });
        showCopilotStarErrorToast();
      }
    },
    [resolveServerId]
  );

  const unstarConversation = useCallback(
    async (conversationId) => {
      dispatch({ type: WORKSPACE_ACTIONS.UNSTAR_CONVERSATION, payload: conversationId });
      const serverId = resolveServerId(conversationId);
      if (!serverId) {
        showCopilotUnstarredToast();
        return;
      }

      try {
        await vertex360CopilotConversationApi.setStarred(serverId, false);
        showCopilotUnstarredToast();
      } catch {
        dispatch({
          type: WORKSPACE_ACTIONS.STAR_CONVERSATION,
          payload: conversationId,
        });
        showCopilotStarErrorToast();
      }
    },
    [resolveServerId]
  );

  const renameConversation = useCallback(
    async (conversationId, title) => {
      const nextTitle = String(title || "").trim();
      if (!nextTitle) return;

      const previousTitle = stateRef.current.conversations.find(
        (item) => item.id === conversationId
      )?.title;
      if (nextTitle === previousTitle) return;

      dispatch({
        type: WORKSPACE_ACTIONS.RENAME_CONVERSATION,
        payload: { id: conversationId, title: nextTitle },
      });

      const serverId = resolveServerId(conversationId);
      if (!serverId) {
        showCopilotRenameSuccessToast();
        return;
      }

      try {
        await vertex360CopilotConversationApi.rename(serverId, nextTitle);
        showCopilotRenameSuccessToast();
      } catch {
        if (previousTitle) {
          dispatch({
            type: WORKSPACE_ACTIONS.RENAME_CONVERSATION,
            payload: { id: conversationId, title: previousTitle },
          });
        }
        showCopilotRenameErrorToast();
      }
    },
    [resolveServerId]
  );

  const deleteConversation = useCallback(
    (conversationId) => {
      const serverId = resolveServerId(conversationId);
      dispatch({ type: WORKSPACE_ACTIONS.DELETE_CONVERSATION, payload: conversationId });
      if (serverId) {
        vertex360CopilotConversationApi.removeMany([serverId]).catch(() => {
          /* best-effort; conversation already removed locally */
        });
      }
    },
    [resolveServerId]
  );

  const deleteSelectedConversations = useCallback(() => {
    if (!state.selectedIds.length) return;
    dispatch({
      type: WORKSPACE_ACTIONS.DELETE_CONVERSATIONS,
      payload: state.selectedIds,
    });
  }, [state.selectedIds]);

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirm((prev) => {
      if (prev.isConfirming) return prev;
      return CLOSED_DELETE_CONFIRM;
    });
  }, []);

  const requestDeleteConversation = useCallback(
    (conversationId) => {
      const conversation = state.conversations.find(
        (item) => item.id === conversationId
      );
      if (!conversation) return;

      setDeleteConfirm({
        isOpen: true,
        variant: "single",
        conversations: [conversation],
        isConfirming: false,
        error: null,
      });
    },
    [state.conversations]
  );

  const requestDeleteSelectedConversations = useCallback(() => {
    if (!state.selectedIds.length) return;

    const conversations = state.conversations.filter((conversation) =>
      state.selectedIds.includes(conversation.id)
    );

    setDeleteConfirm({
      isOpen: true,
      variant: "bulk",
      conversations,
      isConfirming: false,
      error: null,
    });
  }, [state.selectedIds, state.conversations]);

  const confirmDeleteConversations = useCallback(async () => {
    const pending = deleteConfirm;
    if (!pending.conversations.length || pending.isConfirming) return;

    const ids = pending.conversations.map((conversation) => conversation.id);
    const serverIds = pending.conversations
      .map((conversation) => conversation.serverId)
      .filter(Boolean);

    setDeleteConfirm((prev) => ({ ...prev, isConfirming: true, error: null }));

    try {
      if (serverIds.length) {
        await vertex360CopilotConversationApi.removeMany(serverIds);
      }

      if (pending.variant === "bulk") {
        dispatch({ type: WORKSPACE_ACTIONS.DELETE_CONVERSATIONS, payload: ids });
      } else {
        dispatch({
          type: WORKSPACE_ACTIONS.DELETE_CONVERSATION,
          payload: ids[0],
        });
      }

      showCopilotDeleteSuccessToast(ids.length);
      setDeleteConfirm(CLOSED_DELETE_CONFIRM);
    } catch {
      setDeleteConfirm((prev) => ({
        ...prev,
        isConfirming: false,
        error:
          pending.variant === "bulk"
            ? "Could not delete the selected chats. Please try again."
            : "Could not delete this chat. Please try again.",
      }));
    }
  }, [deleteConfirm, dispatch]);

  const setSearchQuery = useCallback((query) => {
    dispatch({ type: WORKSPACE_ACTIONS.SET_SEARCH_QUERY, payload: query });
  }, []);

  const toggleSelectId = useCallback((conversationId) => {
    dispatch({ type: WORKSPACE_ACTIONS.TOGGLE_SELECT_ID, payload: conversationId });
  }, []);

  const selectAllFiltered = useCallback(() => {
    dispatch({
      type: WORKSPACE_ACTIONS.SET_SELECTED_IDS,
      payload: filteredConversations.map((conversation) => conversation.id),
    });
  }, [filteredConversations]);

  const clearSelection = useCallback(() => {
    dispatch({ type: WORKSPACE_ACTIONS.SET_SELECTED_IDS, payload: [] });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      activeConversation,
      starredConversations,
      recentConversations,
      filteredConversations,
      hasConversation,
      isLoadingMessages,
      hasMoreOlder,
      starredSection: state.conversationSections.starred,
      recentSection: state.conversationSections.recent,
      loadMoreStarredConversations,
      loadMoreRecentConversations,
      scrollAnchorRef,
      composerFocusKey,
      setSidebarCollapsed,
      toggleSidebar,
      setView,
      selectConversation,
      startNewChat,
      starConversation,
      unstarConversation,
      renameConversation,
      deleteConversation,
      deleteSelectedConversations,
      deleteConfirm,
      requestDeleteConversation,
      requestDeleteSelectedConversations,
      closeDeleteConfirm,
      confirmDeleteConversations,
      setSearchQuery,
      toggleSelectId,
      selectAllFiltered,
      clearSelection,
      sendMessage,
      retryMessage,
      selectClarifyOption,
      clearActiveFilters,
      loadOlderMessages,
      stopGeneration,
      rateMessageFeedback,
    }),
    [
      state,
      activeConversation,
      starredConversations,
      recentConversations,
      filteredConversations,
      hasConversation,
      isLoadingMessages,
      hasMoreOlder,
      loadMoreStarredConversations,
      loadMoreRecentConversations,
      composerFocusKey,
      setSidebarCollapsed,
      toggleSidebar,
      setView,
      selectConversation,
      startNewChat,
      starConversation,
      unstarConversation,
      renameConversation,
      deleteConversation,
      deleteSelectedConversations,
      deleteConfirm,
      requestDeleteConversation,
      requestDeleteSelectedConversations,
      closeDeleteConfirm,
      confirmDeleteConversations,
      setSearchQuery,
      toggleSelectId,
      selectAllFiltered,
      clearSelection,
      sendMessage,
      retryMessage,
      selectClarifyOption,
      clearActiveFilters,
      loadOlderMessages,
      stopGeneration,
      rateMessageFeedback,
    ]
  );

  return createElement(Vertex360CopilotWorkspaceContext.Provider, { value }, children);
};

export const useVertex360CopilotWorkspace = () => {
  const context = useContext(Vertex360CopilotWorkspaceContext);
  if (!context) {
    throw new Error("useVertex360CopilotWorkspace must be used within Vertex360CopilotWorkspaceProvider");
  }
  return context;
};
